// Third-party
import React, { Component } from 'react';
import classNames from 'classnames';

// Components
import { withErrorBoundary } from '../../errors';
import LineChart from '../../charts/deprecated/charts/LineChart';

// Utils
import { BMOi18n, GIF_GRAPH_CONFIGS } from '../../../../utils/bmo-gif/constants';
import { getMaturityGuaranteeDisclaimer, getDeathBenefitDisclaimer, scenarioHasWithdrawal } from '../../../../utils/bmo-gif/index';
import { getAge } from '../../../../utils/time';
import { formatNumber } from '../../../../utils/i18n';
import BMOGIFScenario from '@/types/bmo-gif/bmo-gif-scenario';
import { LineChartDatum, LineChartOptions } from '../../charts/LineChart';

type Props = {
  scenario: BMOGIFScenario;
  dark?: boolean;
  height?: number;
  locale: string;
  tooltipDisclaimer?: boolean;
  planOption?: string;
  isAdverse?: boolean;
  readOnly?: boolean;
};

class BMOGifChart extends Component<Props> {
  getChartOptions = (dob: Date): LineChartOptions => ({
    chart: {
      height: this.props.height,
    },
    xAxis: {
      crosshair: {
        snap: false,
      },
      labels: {
        formatter() {
          const age = getAge(dob, undefined, new Date(this.value));
          return `${new Date(this.value).getUTCFullYear()}<br>${age}`;
        },
      },
    },
    yAxis: {
      title: {
        text: null,
      },
      labels: {
        x: -5,
        y: -8,
        formatter() {
          return formatNumber(Number(this.value), 0);
        },
      },
      startOnTick: true,
      opposite: true,
    },
    tooltip: {
      pointFormat:
        '<span style="color:{point.color};}">\u25CF</span>&nbsp;<span data-private data-dd-privacy="mask">{series.name}</span>: <b style="color:#A4A7B7;">{point.y}</b><br/>',
    },
  });

  formatItems = (): LineChartDatum[] => {
    const { scenario, locale, isAdverse } = this.props;
    const data = isAdverse ? scenario.adverseData : scenario.data;
    const graphConfigs = Object.values(GIF_GRAPH_CONFIGS);
    for (const config of graphConfigs) {
      if (config.key === 'netWithdrawal') {
        config.hidden = !scenarioHasWithdrawal(scenario);
      }
      if (config.key === 'netDeposit') {
        config.hidden = scenario.deposit.amount === 0;
      }
    }
    const lineChartData: LineChartDatum[] = [];
    for (const config of graphConfigs.filter(item => !item.hidden)) {
      lineChartData.push({
        /* We calculate an additional data point after Dec 31st of the last term year,
         this shouldn't be displayed in graph, so we slice the last element off.
         */
        data: data.map(item => [new Date(item.date).getTime(), item[config.key]]).slice(0, -1),
        color: config.color,
        name: BMOi18n[locale].SCENARIOS[config.i18nKey],
        type: 'line',
        dashStyle: 'Solid',
        lineWidth: 2,
        tooltip: config.tooltip && config.tooltip[locale],
      });
    }
    return lineChartData;
  };

  render() {
    const { dark, tooltipDisclaimer, planOption, locale, scenario, readOnly } = this.props;

    return (
      <div className={classNames('gif-chart chart', { dark })}>
        <LineChart data={this.formatItems()} chartOptions={this.getChartOptions(scenario.dob)} showDatePickers={false} readOnly={readOnly} values={[]} />

        {!tooltipDisclaimer && (
          <div className="gif-chart-disclaimers">
            {scenario.deposit.amount > 0 && <span>{`*${BMOi18n[locale].GIF_GRAPH.DEPOSIT}`}</span>}
            <span>{`**${getMaturityGuaranteeDisclaimer(locale, planOption)}`}</span>
            <span>{`***${getDeathBenefitDisclaimer(locale, planOption)}`}</span>
          </div>
        )}
      </div>
    );
  }
}

export default withErrorBoundary(BMOGifChart);
